import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { VerificationHeader } from './VerificationApproval/components/VerificationHeader';
import { VerificationFilters } from './VerificationApproval/components/VerificationFilters';
import { VerificationList } from './VerificationApproval/components/VerificationList';
import { ConfirmationDialog } from './VerificationApproval/components/ConfirmationDialog';
import { useVerificationData } from './VerificationApproval/hooks/useVerificationData';
import { useVerificationFilters } from './VerificationApproval/hooks/useVerificationFilters';
import { exportVerifications } from './VerificationApproval/utils/export';
import { useLanguage } from '../contexts/LanguageContext';
import { Verification } from '../types/verification';
import toast from 'react-hot-toast';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const VerificationApprovalPage = () => {
  const { translations } = useLanguage();
  const { verifications, setVerifications, loading } = useVerificationData();
  const {
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    typeFilter,
    setTypeFilter,
    filteredVerifications
  } = useVerificationFilters(verifications);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    verification: Verification | null;
    action: 'approve' | 'reject' | 'delete';
    rejectionReason?: string;
  }>({
    isOpen: false,
    verification: null,
    action: 'approve',
  });

  const handleStatusChange = async (
    verification: Verification,
    newStatus: 'approved' | 'rejected',
    rejectionReason?: string
  ) => {
    try {
      const verificationRef = doc(firestore, 'verifications', verification.id);
      const userId = localStorage.getItem('userId');

      if (!userId) {
        throw new Error(
          translations?.verificationApproval?.errors?.notAuthenticated ||
            'User not authenticated'
        );
      }

      const updates: Partial<Verification> = {
        status: newStatus,
        reviewedBy: userId,
        reviewedAt: Date.now(),
        updatedAt: Date.now(),
      };

      if (newStatus === 'rejected' && rejectionReason) {
        updates.rejectionReason = rejectionReason;
      }

      await updateDoc(verificationRef, updates);

      setVerifications(prev =>
        prev.map(v =>
          v.id === verification.id ? { ...v, ...updates } : v
        )
      );

      toast.success(
        newStatus === 'approved'
          ? translations?.verificationApproval?.success?.approved ||
              'Verification approved successfully'
          : translations?.verificationApproval?.success?.rejected ||
              'Verification rejected successfully'
      );
    } catch (error: any) {
      console.error('Error updating verification status:', error);
      toast.error(
        translations?.verificationApproval?.errors?.updateFailed ||
          'Failed to update verification status'
      );
    }
  };

  const handleDelete = async (verification: Verification) => {
    try {
      await deleteDoc(doc(firestore, 'verifications', verification.id));
      
      setVerifications(prev => prev.filter(v => v.id !== verification.id));
      
      toast.success(
        translations?.verificationApproval?.success?.deleted ||
          'Verification deleted successfully'
      );
    } catch (error) {
      console.error('Error deleting verification:', error);
      toast.error(
        translations?.verificationApproval?.errors?.deleteFailed ||
          'Failed to delete verification'
      );
    }
  };

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success(
        translations?.verificationApproval?.success?.copied ||
          'Copied to clipboard'
      );
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      toast.error(
        translations?.verificationApproval?.errors?.copyFailed ||
          'Failed to copy'
      );
    }
  };

  const handleExport = () => {
    const success = exportVerifications(filteredVerifications, translations);
    if (success) {
      toast.success(
        translations?.verificationApproval?.success?.exportSuccess ||
          'Data exported successfully'
      );
    } else {
      toast.error(
        translations?.verificationApproval?.errors?.exportFailed ||
          'Failed to export data'
      );
    }
  };

  // Pagination calculation
  const totalPages = Math.ceil(filteredVerifications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedVerifications = filteredVerifications.slice(startIndex, startIndex + itemsPerPage);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <VerificationHeader />
      
      <VerificationFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
        onExport={handleExport}
      />

      <VerificationList
        verifications={paginatedVerifications}
        onApprove={(verification) =>
          setConfirmDialog({
            isOpen: true,
            verification,
            action: 'approve',
          })
        }
        onReject={(verification) =>
          setConfirmDialog({
            isOpen: true,
            verification,
            action: 'reject',
          })
        }
        onDelete={(verification) =>
          setConfirmDialog({
            isOpen: true,
            verification,
            action: 'delete',
          })
        }
        copiedField={copiedField}
        onCopy={handleCopy}
      />

      {/* Pagination Controls */}
      {filteredVerifications.length > itemsPerPage && (
        <div className="flex items-center justify-between bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center gap-4">
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-2 py-1 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
            >
              {[5, 10, 25, 50].map(size => (
                <option key={size} value={size}>
                  {size} {translations?.pagination?.perPage || 'per page'}
                </option>
              ))}
            </select>
            
            <span className="text-sm text-gray-500">
              {translations?.pagination?.showing || 'Showing'}{' '}
              {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredVerifications.length)}{' '}
              {translations?.pagination?.of || 'of'}{' '}
              {filteredVerifications.length}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className={`p-1 rounded-lg ${
                currentPage === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronRight size={20} />
            </button>

            <span className="text-sm text-gray-700">
              {translations?.pagination?.page || 'Page'} {currentPage}{' '}
              {translations?.pagination?.of || 'of'} {totalPages}
            </span>

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className={`p-1 rounded-lg ${
                currentPage === totalPages
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronLeft size={20} />
            </button>
          </div>
        </div>
      )}

      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        verification={confirmDialog.verification}
        action={confirmDialog.action}
        rejectionReason={confirmDialog.rejectionReason}
        onClose={() =>
          setConfirmDialog({
            isOpen: false,
            verification: null,
            action: 'approve',
          })
        }
        onConfirm={async () => {
          if (!confirmDialog.verification) return;

          if (confirmDialog.action === 'delete') {
            await handleDelete(confirmDialog.verification);
          } else {
            await handleStatusChange(
              confirmDialog.verification,
              confirmDialog.action === 'approve' ? 'approved' : 'rejected',
              confirmDialog.rejectionReason
            );
          }

          setConfirmDialog({
            isOpen: false,
            verification: null,
            action: 'approve',
          });
        }}
        onRejectionReasonChange={(reason) =>
          setConfirmDialog((prev) => ({
            ...prev,
            rejectionReason: reason,
          }))
        }
      />
    </div>
  );
};
